import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { debounce } from '@theme/utilities';

const DISMISSED_KEY = 'wbs-newsletter-modal-dismissed';
const SUBSCRIBED_KEY = 'wbs-newsletter-modal-subscribed';
const FORM_ID = 'NewsletterModalForm';
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

/**
 * Newsletter opt-in modal with configurable delay, scroll, and exit-intent triggers.
 *
 * @extends {DialogComponent}
 */
class NewsletterModal extends DialogComponent {
  /** @type {boolean} */
  #opened = false;

  /** @type {boolean} */
  #delayMet = false;

  /** @type {boolean} */
  #scrollMet = false;

  /** @type {boolean} */
  #exitMet = false;

  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {number | undefined} */
  #delayTimer;

  /** @type {boolean} */
  #formSuccess = false;

  connectedCallback() {
    super.connectedCallback();

    const isEditorPreview = this.#isEditorPreview();
    this.#formSuccess = this.#isFormSuccessFromUrl() || Boolean(document.getElementById('newsletter-modal-email-success'));

    if (this.#formSuccess && !isEditorPreview) {
      this.#handleFormSuccess();
      return;
    }

    if (document.getElementById('newsletter-modal-email-error')) {
      this.#opened = true;
      this.showDialog();
      return;
    }

    if (!isEditorPreview && this.#shouldHide()) return;

    this.addEventListener(DialogCloseEvent.eventName, this.#onClose);

    if (isEditorPreview) {
      this.#scheduleEditorPreview();
      return;
    }

    this.#setupTriggers();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController?.abort();
    if (this.#delayTimer) window.clearTimeout(this.#delayTimer);
    this.removeEventListener(DialogCloseEvent.eventName, this.#onClose);
  }

  #isDesignMode() {
    return (
      this.dataset.designMode === 'true' ||
      document.documentElement.classList.contains('shopify-design-mode') ||
      Boolean(window.Shopify?.designMode)
    );
  }

  #isEditorPreview() {
    return this.#isDesignMode() && this.dataset.previewInEditor === 'true';
  }

  #scheduleEditorPreview() {
    const open = () => this.#tryOpen(true);

    if (this.#delayTimer) window.clearTimeout(this.#delayTimer);

    if (document.getElementById('home-preloader')?.hasAttribute('active')) {
      const preloader = document.getElementById('home-preloader');
      const observer = new MutationObserver(() => {
        if (!preloader?.hasAttribute('active')) {
          observer.disconnect();
          this.#delayTimer = window.setTimeout(open, 200);
        }
      });
      observer.observe(preloader, { attributes: true, attributeFilter: ['active'] });
      this.#delayTimer = window.setTimeout(() => {
        observer.disconnect();
        open();
      }, 3000);
      return;
    }

    this.#delayTimer = window.setTimeout(open, 200);
  }

  #setupTriggers() {
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    const delaySeconds = Number(this.dataset.delaySeconds) || 0;
    const scrollPercent = Number(this.dataset.scrollPercent) || 0;
    const exitIntentEnabled = this.dataset.exitIntent === 'true';

    if (delaySeconds > 0) {
      this.#delayTimer = window.setTimeout(() => {
        this.#delayMet = true;
        this.#checkTriggers();
      }, delaySeconds * 1000);
    } else {
      this.#delayMet = true;
    }

    if (scrollPercent > 0) {
      window.addEventListener('scroll', this.#onScroll, { passive: true, signal });
      this.#onScroll();
    }

    if (exitIntentEnabled && !this.#isTouchDevice()) {
      document.addEventListener('mouseout', this.#onMouseOut, { signal });
    }

    if (delaySeconds === 0 && scrollPercent === 0 && !exitIntentEnabled) {
      this.#checkTriggers();
    }
  }

  #onScroll = debounce(() => {
    const scrollPercent = Number(this.dataset.scrollPercent) || 0;
    if (scrollPercent <= 0) return;

    const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollHeight <= 0) {
      this.#scrollMet = true;
      this.#checkTriggers();
      return;
    }

    const currentPercent = (window.scrollY / scrollHeight) * 100;
    if (currentPercent >= scrollPercent) {
      this.#scrollMet = true;
      this.#checkTriggers();
    }
  }, 100);

  /** @param {MouseEvent} event */
  #onMouseOut = (event) => {
    if (event.clientY > 0) return;
    if (event.relatedTarget !== null) return;

    this.#exitMet = true;
    this.#checkTriggers();
  };

  #checkTriggers() {
    if (this.#opened) return;

    const mode = this.dataset.triggerMode || 'any';
    const scrollEnabled = (Number(this.dataset.scrollPercent) || 0) > 0;
    const exitEnabled = this.dataset.exitIntent === 'true' && !this.#isTouchDevice();

    const triggers = [
      { enabled: true, met: this.#delayMet },
      { enabled: scrollEnabled, met: this.#scrollMet },
      { enabled: exitEnabled, met: this.#exitMet },
    ];

    const enabledTriggers = triggers.filter((trigger) => trigger.enabled);

    if (enabledTriggers.length === 0) {
      this.#tryOpen();
      return;
    }

    const shouldOpen =
      mode === 'all'
        ? enabledTriggers.every((trigger) => trigger.met)
        : enabledTriggers.some((trigger) => trigger.met);

    if (shouldOpen) {
      this.#tryOpen();
    }
  }

  /**
   * @param {boolean} [isEditorPreview]
   */
  #tryOpen(isEditorPreview = false) {
    if (this.#opened) return;
    if (!isEditorPreview && this.#shouldHide()) return;

    if (!isEditorPreview && document.getElementById('home-preloader')?.hasAttribute('active')) return;

    if (!isEditorPreview && document.querySelector('dialog[open]')) return;

    this.#opened = true;
    this.#abortController?.abort();
    this.showDialog();
  }

  #onClose = () => {
    if (this.#isEditorPreview()) {
      this.#opened = false;
      return;
    }

    if (this.#formSuccess) return;

    this.#persistDismissal();
  };

  #handleFormSuccess() {
    this.#persistSubscription();
    this.#cleanFormSuccessUrl();
    this.#ensureSuccessMessage();
    this.#showSuccessState();
    this.#opened = true;
    this.showDialog();
    this.addEventListener(DialogCloseEvent.eventName, this.#onClose);
    this.#focusSuccessMessage();
  }

  #showSuccessState() {
    this.querySelector('#NewsletterModalForm .email-signup__input-group')?.setAttribute('hidden', '');
  }

  #ensureSuccessMessage() {
    if (document.getElementById('newsletter-modal-email-success')) return;

    const form = this.querySelector('#NewsletterModalForm');
    const inputGroup = form?.querySelector('.email-signup__input-group');
    const message = document.createElement('div');

    message.id = 'newsletter-modal-email-success';
    message.className = 'email-signup__message';
    message.tabIndex = -1;
    message.innerHTML = `<p class="email-signup__message-text">${this.dataset.successMessage || 'Thanks for subscribing.'}</p>`;

    if (inputGroup) inputGroup.hidden = true;
    form?.appendChild(message);
  }

  #isFormSuccessFromUrl() {
    const url = new URL(window.location.href);

    return url.searchParams.get('customer_posted') === 'true' && url.hash === `#${FORM_ID}`;
  }

  #cleanFormSuccessUrl() {
    const url = new URL(window.location.href);

    if (url.searchParams.get('customer_posted') !== 'true' || url.hash !== `#${FORM_ID}`) return;

    url.searchParams.delete('customer_posted');
    url.hash = '';
    window.history.replaceState({}, '', `${url.pathname}${url.search}`);
  }

  #focusSuccessMessage() {
    requestAnimationFrame(() => {
      document.getElementById('newsletter-modal-email-success')?.focus();
    });
  }

  #persistSubscription() {
    if (this.#isDesignMode()) return;
    if (this.dataset.hideSubscribed !== 'true') return;

    localStorage.setItem(SUBSCRIBED_KEY, '1');
  }

  #persistDismissal() {
    if (this.#isDesignMode()) return;

    const frequency = this.dataset.frequency || '7days';

    if (frequency === 'always') return;

    if (frequency === 'session') {
      sessionStorage.setItem(DISMISSED_KEY, '1');
      return;
    }

    localStorage.setItem(DISMISSED_KEY, String(Date.now()));
  }

  #shouldHide() {
    if (this.#isDesignMode()) return false;

    return this.#isSubscribed() || this.#isDismissed();
  }

  #isSubscribed() {
    if (this.dataset.hideSubscribed !== 'true') return false;

    return localStorage.getItem(SUBSCRIBED_KEY) === '1';
  }

  #isDismissed() {
    const frequency = this.dataset.frequency || '7days';

    if (frequency === 'always') return false;

    if (frequency === 'session') {
      return sessionStorage.getItem(DISMISSED_KEY) === '1';
    }

    const timestamp = localStorage.getItem(DISMISSED_KEY);
    if (!timestamp) return false;

    return Date.now() - Number(timestamp) < SEVEN_DAYS_MS;
  }

  #isTouchDevice() {
    return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  }
}

if (!customElements.get('newsletter-modal')) {
  customElements.define('newsletter-modal', NewsletterModal);
}
